import { Platform, SofaRange, ApplicationParams, ParentParams } from 'sofa-reborn-react';
import { ApplicationUtils } from '../util/ApplicationUtils/ApplicationUtils';
import { DesignSourceEnum } from '@insights/insights-data-provider';

export class UrlService {
	public static getSearchString(): string {
		const locationSearch =
			window.parent && !ApplicationUtils.isProduction()
				? window.parent.location.search
				: window.location.search;
		return locationSearch;
	}

	public static getParentSearchString(): string {
		return window.parent.location.search;
	}

	public static getPlatform(): 'web' | 'kiosk' {
		const params = new URLSearchParams(window.location.search);
		const platform = params.get('uiPlatform') || UrlService.getPlatformFromPath();

		if (platform) {
			return platform as Platform;
		}

		return 'web';
	}

	public static getPlatformFromPath(): Platform | null {
		const match = window.location.pathname.match(/\/(web|kiosk)\//);
		if (!match) {
			return null;
		}
		return match[1] as Platform;
	}

	public static getSofaRange(): SofaRange {
		const params = new URLSearchParams(window.location.search);
		const range = params.get('range') || UrlService.getRangeFromPath();

		if (range) {
			return range as SofaRange;
		}

		return 'kivik';
	}

	public static getRangeFromPath(): SofaRange | null {
		const match = window.location.pathname.match(/\/(kivik|jattebo)\//);
		if (!match) {
			return null;
		}
		return match[1] as SofaRange;
	}

	public static getStoreId(): string {
		const query = new URLSearchParams(UrlService.getSearchString());
		return query.get('storeId') || '';
	}

	public static removeParam(parameter: string, url: string): string {
		let result = url.split('?')[0];
		const queryString = url.indexOf('?') !== -1 ? url.split('?')[1] : '';
		let params: string[] = [];
		let param = '';
		if (queryString !== '') {
			params = queryString.split('&');
			for (let i = params.length - 1; i >= 0; i -= 1) {
				param = params[i].split('=')[0];
				if (param === parameter) {
					params.splice(i, 1);
				}
			}
			result = result + '?' + params.join('&');
		}

		return result;
	}

	public static removeConfigurationId(url: string): string {
		const idInUrl = this.getVpcOrSprId();
		return url.replace('#' + idInUrl, '');
	}

	public static getVpcOrSprId(): string | null {
		/* To upper case is for blank to match regex */
		const hash = window.location.hash.toUpperCase();
		// If there are any vpc code or spr id in the url, we get it
		const match = hash.match(/^#\/?([A-Z\d]+)/);
		let id = match && match[1];
		if (id && id.length > 6) {
			// spr in url
			return id.replace('S', '');
		} else {
			// vpc, blank or nothing
			id = id === 'BLANK' ? id.toLowerCase() : id;
			return id || null;
		}
	}

	public static isSPR(): boolean {
		/* To upper case is for blank to match regex */
		const hash = window.location.hash.toUpperCase();
		// If there are any vpc code or spr id in the url, we get it
		const match = hash.match(/^#\/?([A-Z\d]+)/);
		const id = match && match[1];
		if (id) {
			return id.toUpperCase().includes('S');
		}
		return false;
	}

	public static getReplacedIdHash(id: string): string {
		const hash = window.location.hash;
		if (hash) {
			const idInUrl = this.getVpcOrSprId();
			if (idInUrl) {
				// current hash already has a vpc or spr so we replace it
				if (idInUrl.length > 6) {
					if (hash.charAt(1) === 's') {
						return hash.replace(idInUrl, id).replace('s', '');
					} else if (hash.charAt(1) === 'S') {
						return hash.replace(idInUrl, id).replace('S', '');
					}
					return hash.replace(idInUrl, id);
				}
				return hash.replace(idInUrl, id);
			} else {
				// if the hash has blank, we remove it
				return '#' + id + hash.substring(1).replace('blank', '');
			}
		} else {
			return '#' + id;
		}
	}

	public static setBrowserWindowHash(hash: string, search = ''): void {
		try {
			// For reference: http://stackoverflow.com/a/9282379
			// for modern browsers
			if ('replaceState' in parent.window.history) {
				if (String(hash).charAt(0) !== '#') {
					hash = '#' + hash;
				}
				parent.window.history.replaceState('', '', search + hash);
			}
			// not so modern browsers, e.g. mobile browsers
			else {
				const oldHash = parent.window.location.hash;
				if (parent.window.location.hash !== oldHash) {
					const history = parent.window.history as History;
					history.back();
				}
				parent.window.location.hash = hash;
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.log(e);
		}
	}

	public static getApplicationParams(): ApplicationParams {
		const query = new URLSearchParams(UrlService.getSearchString());
		const hash = window.location.hash;

		return {
			activeScreenSaver:
				query.has('activeScreenSaver') || hash.includes('activeScreenSaver') || null,
			showDebug: query.has('showDebug') || null,
			assetFolder: query.get('assetFolder') || null,
			useIndividualAssets: query.has('useIndividualAssets') || null,
			environmentalLightning: query.get('environmentalLightning') || null,
			lightIntensity: query.get('lightIntensity') || null,
			features: query.get('features') || null,
			fakeData: query.has('fakeData') || null,
			showSummaryPage: query.has('showSummaryPage') || null,
			showIpexGallery: query.has('ipexGallery') || null,
			idleTime: query.get('idleTime') || null,
			isInUpptackaPlatform: query.get('origin') === 'upptacka' || null,
			fromNavigator: query.get('fromNavigator') || null,
			disableBulkFetch: query.has('disableBulkFetch') || null,
			usePersistedConfig: query.has('usePersistedConfig') || null,
			zipCode: query.get('zipCode') || null,
			kioskId: query.get('kioskId') || null,
			cover: query.has('cover') || null,
			vpcSource: (query.get('vpcSource') as DesignSourceEnum) || null,
			loggedIn: query.has('loggedin') || null,
			callback: query.has('callback') || null,
			error: query.get('error') || null,
			computerId: query.get('computerId') || null,
		};
	}

	public static getParentParams(): ParentParams | null {
		try {
			const query = new URLSearchParams(UrlService.getParentSearchString());

			return {
				error: query.get('error') || null,
				callback: query.has('callback') || null,
				vpcSource: (query.get('vpcSource') as DesignSourceEnum) || null,
			};
		} catch {
			return null;
		}
	}

	public static getRedirectUri = (): string => {
		if (ApplicationUtils.isProduction()) {
			return window.parent.location.origin + window.parent.location.pathname + '?callback';
		} else {
			return window.parent.location.href.split('#')[0] + '&callback';
		}
	};
}
